<template>
  <div class="col-sm-12">
    <div class="card shadow-sm report-page">
      <!-- Filters Section -->
      <div class="dropdown-container">

          <!-- FSM -->
          <div class="dropdown-item">
            <label for="fsmSelect">FSM</label>
            <select id="fsmSelect" v-model="selectedFsm" @change="onFsmChange" :disabled="isFsmLocked">
              <option value="">Select FSM</option>
              <option v-if="isOtherUser" :value="ALL">All FSMs</option>
              <option v-for="fsm in fsmList" :key="fsm.profile_id" :value="String(fsm.profile_id)">
                {{ fsm.name }}
              </option>
            </select>
          </div>

          <!-- ASM -->
          <div class="dropdown-item">
            <label for="asmSelect">ASM</label>
            <select id="asmSelect" v-model="selectedAsm" @change="onAsmChange" :disabled="isAsmLocked">
              <option value="">Select ASM</option>
              <option v-if="isFsmUser || isOtherUser" :value="ALL">All ASMs</option>
              <option v-for="asm in asmList" :key="asm.profile_id" :value="String(asm.profile_id)">
                {{ asm.name }}
              </option>
            </select>
          </div>

          <!-- Distributor -->
          <div class="dropdown-item">
            <label for="distributorSelect">Distributor</label>
            <select id="distributorSelect" v-model="selectedDistributor" :disabled="isDistributorLocked"
              @change="onDistributorChange">
              <option value="">Select Distributor</option>
              <option v-if="isFsmUser || isAsmUser || isOtherUser" :value="ALL">All Distributors</option>
              <option v-for="d in distributorList" :key="d.profile_id" :value="String(d.profile_id)">
                {{ d.name }}
              </option>
            </select>
          </div>

          <!-- Route -->
          <div class="dropdown-item">
            <label for="routeSelect">Route</label>
            <select id="routeSelect" v-model="selectedRoute"
              :disabled="!routeList.length || !selectedDistributor || selectedDistributor === ALL">
              <option value="">All Routes</option>
              <option v-for="r in routeList" :key="r.route_id ?? r.id" :value="String(r.route_id ?? r.id)">
                {{ r.route_name ?? r.name ?? ("Route #" + (r.route_id ?? r.id)) }}
              </option>
            </select>
          </div>

          <!-- Start Date -->
          <div class="dropdown-item">
            <label>Start Date</label>
            <input type="date" v-model="startDate" :max="currentDate" @change="handleDateChange"
              @blur="validateStartDate(true)" />
          </div>

          <!-- End Date -->
          <div class="dropdown-item">
            <label>End Date</label>
            <input type="date" v-model="endDate" :max="currentDate" @change="handleDateChange"
              @blur="validateEndDate(true)" />
          </div>

          <!-- Rep Name -->
          <div class="dropdown-item">
            <label for="repSelect">Rep Name</label>
            <select id="repSelect" v-model="selectedRep"
              :disabled="!repList.length || !selectedDistributor || selectedDistributor === ALL">
              <option value="">All Reps</option>
              <option v-for="r in repList" :key="r.profile_id ?? r.id" :value="String(r.profile_id ?? r.id)">
                {{ r.rep_name ?? r.name ?? r.first_name ?? ("Rep #" + (r.profile_id ?? r.id)) }}
              </option>
            </select>
          </div>

          <!-- Search Button -->
          <div class="search-container">
            <button type="button" @click="performSearch" class="btn button-default" :disabled="loading">
              <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i>
              {{ loading ? "Searching..." : "Search" }}
            </button>
            <button type="button" class="btn button-export" :disabled="!reportData.length || loading" @click="exportToExcel">
              Export to Excel
            </button>
          </div>
      </div>

      <!-- Loading Widget matching dayreport_form.vue -->
      <div v-if="loading" class="loading-container mt-4">
        <div class="loading-card">
          <div class="loading-spinner-wrapper">
            <i class="fa fa-spinner fa-spin loading-icon"></i>
          </div>
          <h6 class="loading-title">
            <span>Loading Distributor Wise Sales Report...</span>
          </h6>
          <p class="loading-subtitle">Please wait while the records are being retrieved.</p>
        </div>
      </div>

      <!-- Results Table -->
      <div v-if="!loading && reportData.length && items.length" class="table-scroll mt-3">
        <table class="table table-striped table-bordered">
          <thead>
            <tr>
              <th>Distributor</th>
              <th>Route</th>
              <th>Rep Name</th>
              <th>Date</th>

              <th v-for="it in items" :key="it.item_id" class="text-center">
                {{ it.item_name }}
              </th>

              <th class="text-end">Total Qty</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(row, i) in reportData" :key="i">
              <td>{{ row.distributor_name || "-" }}</td>
              <td>{{ row.route_name || "-" }}</td>
              <td>{{ row.rep_name || "-" }}</td>
              <td>{{ row.invoice_date || "-" }}</td>

              <td v-for="it in items" :key="it.item_id" class="text-end">
                {{ formatQty(row?.[String(it.item_id)] ?? 0) }}
              </td>

              <td class="text-end">{{ formatQty(row.total_qty ?? 0) }}</td>
            </tr>

            <tr class="total-row">
              <td colspan="4" class="text-end"><strong>TOTAL</strong></td>

              <td v-for="it in items" :key="it.item_id" class="text-end">
                <strong>{{ formatQty(columnTotal(String(it.item_id))) }}</strong>
              </td>

              <td class="text-end"><strong>{{ formatQty(grandTotalQty) }}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Modern Empty State matching dayreport_form.vue -->
      <div v-else-if="!loading && searched && (!reportData.length || !items.length)" class="no-data-container mt-4">
        <div class="no-data-card">
          <div class="no-data-icon-wrapper">
            <i class="fa fa-folder-open-o no-data-icon"></i>
          </div>
          <h5 class="no-data-title">No Data Found</h5>
          <p class="no-data-text">
            There are no sales records matching your selected filters.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import {
  fetchFsmList,
  fetchAsmList,
  fetchDistributorList,
  fetchDissaleReport,
  fetchRouteList,
  fetchSalesRepList,
} from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  name: "DistributorWiseSalesForm",

  data() {
    const ALL = "__all__";
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      token: "",
      fsmList: [],
      asmList: [],
      distributorList: [],
      routeList: [],
      repList: [],
      selectedFsm: "",
      selectedAsm: "",
      selectedDistributor: "",
      selectedRoute: "",
      selectedRep: "",
      startDate: currentDate,
      endDate: currentDate,
      currentDate,
      loading: false,
      items: [],       
      reportData: [],
      userLabel: "",
      roleKey: "OTHER",
      myProfileId: Number(localStorage.getItem("profile_id") || 0),
      isFsmLocked: false,
      isAsmLocked: false,
      isDistributorLocked: false,
      searched: false,
      ALL,
      redirecting: false,
    };
  },

  computed: {
    isDistributorUser() { return this.roleKey === "DIS"; },
    isAsmUser() { return this.roleKey === "ASM"; },
    isFsmUser() { return this.roleKey === "FSM"; },
    isOtherUser() { return this.roleKey === "OTHER"; },

    grandTotalQty() {
      return this.reportData.reduce((sum, r) => sum + (Number(r.total_qty) || 0), 0);
    },
  },

  mounted() {
    this.initLoginAndLoadFsm();
  },

  methods: {
    toast(type, message) {
      const t = useToast();
      type === "error"
        ? t.error(message, { position: "top-right", timeout: 3000 })
        : t.success(message, { position: "top-right", timeout: 3000 });
    },

    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return false;

      // block manual formats like 2025/11/31
      if (dateStr.includes("/")) return false;

      // must be YYYY-MM-DD
      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return false;

      const y = Number(m[1]);
      const mo = Number(m[2]);
      const d = Number(m[3]);

      // detect overflow dates like 2025-11-31
      const dt = new Date(y, mo - 1, d);
      return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
    },

    validateStartDate(showToast = true) {
      if (!this.startDate) return true;

      if (!this.isValidISODate(this.startDate)) {
        if (showToast) this.toast("error", "Invalid Start Date");
        this.startDate = "";
        return false;
      }

      if (this.startDate > this.currentDate) {
        if (showToast) this.toast("error", "Start Date cannot be in the future");
        this.startDate = "";
        return false;
      }

      return true;
    },

    validateEndDate(showToast = true) {
      if (!this.endDate) return true;

      if (!this.isValidISODate(this.endDate)) {
        if (showToast) this.toast("error", "Invalid End Date");
        this.endDate = "";
        return false;
      }

      if (this.endDate > this.currentDate) {
        if (showToast) this.toast("error", "End Date cannot be in the future");
        this.endDate = "";
        return false;
      }

      return true;
    },

    handleDateChange() {
      // validate each field silently first
      this.validateStartDate(false);
      this.validateEndDate(false);

      // validate range if both exist
      if (this.startDate && this.endDate) {
        if (this.endDate < this.startDate) {
          this.toast("error", "End date cannot be before start date.");
          this.endDate = "";
        }
      }
    },

    validateDatesForSearch() {
      // REQUIRED
      if (!this.startDate) { this.toast("error", "Please select valid Start Date"); return false; }
      if (!this.endDate) { this.toast("error", "Please select valid End Date"); return false; }

      // VALID
      if (!this.validateStartDate(true)) return false;
      if (!this.validateEndDate(true)) return false;

      // RANGE
      if (this.endDate < this.startDate) {
        this.toast("error", "End date cannot be before start date.");
        return false;
      }

      return true;
    },

    // ========= AUTH helpers =========
    bodyOf(x) { return x?.data ?? x; },
    msgOf(x) {
      const b = this.bodyOf(x?.response?.data ?? x);
      return String(b?.message ?? b?.error ?? x?.message ?? "");
    },

    //   const http = x?.response?.status ?? x?.status;
    //   const b = this.bodyOf(x?.response?.data ?? x);
    //   const statusVal = String(b?.status ?? "").toLowerCase();
    //   const msg = this.msgOf(x).toLowerCase();

    //   return (
    //     http === 401 || http === 419 || http === 440 || http === 498 ||
    //     statusVal === "failed" ||
    //     /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/.test(msg)
    //   );
    // },

    isUnauthorizedAny(x) {
      const http = x?.response?.status ?? x?.status;
      const msg = this.msgOf(x).toLowerCase();

      const authMsg =
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/;

      // ✅ KEY FIX: only logout on 401 if message clearly indicates auth/token issue
      if (http === 401) return authMsg.test(msg);

      return (
        http === 419 ||
        http === 440 ||
        http === 498 ||
        authMsg.test(msg)
      );
    },

    redirectToLogin(message) {
      if (this.redirecting) return;
      this.redirecting = true;

      this.toast("error", message || "Session expired. Please login again.");

      localStorage.removeItem("token_set");
      localStorage.removeItem("profile_id");
      localStorage.removeItem("user_label");
      localStorage.removeItem("userLabel");
      localStorage.removeItem("role");

      if (this.$router) this.$router.replace("/login");
      else window.location.replace("/login");
    },
    handleUnauthorized(x) {
      if (!this.isUnauthorizedAny(x)) return false;
      this.redirectToLogin();
      return true;
    },

    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(labelFromStorage);
      const labelUpper = String(this.userLabel || "").toUpperCase().trim();

      if (labelUpper === "DISTRIBUTOR" || labelUpper.startsWith("DISTRIBUTOR") || labelUpper === "DIS" || labelUpper.startsWith("DIS_")) {
        this.roleKey = "DIS";
      } else if (labelUpper === "ASM" || labelUpper.startsWith("ASM_")) {
        this.roleKey = "ASM";
      } else if (labelUpper === "FSM" || labelUpper.startsWith("FSM_")) {
        this.roleKey = "FSM";
      } else {
        this.roleKey = "OTHER";
      }
    },

    validateFiltersOneByOne() {
      if (!this.selectedFsm) { this.toast("error", "Please select FSM."); return false; }
      if (!this.selectedAsm) { this.toast("error", "Please select ASM."); return false; }
      if (!this.selectedDistributor) { this.toast("error", "Please select Distributor."); return false; }
      return true;
    },

    // ========= INIT =========
    async initLoginAndLoadFsm() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token_set") || "";
        if (!token) return this.redirectToLogin("Session expired. Please login again.");

        this.token = token;
        this.myProfileId = Number(localStorage.getItem("profile_id") || 0);
        this.initRoleFromStorage();

        await this.loadFsmList();
        if (this.redirecting) return;

        this.selectedFsm = "";
        this.selectedAsm = "";
        this.selectedDistributor = "";

        this.selectedRoute = "";
        this.selectedRep = "";
        this.routeList = [];
        this.repList = [];

        this.asmList = [];
        this.distributorList = [];
        this.items = [];
        this.reportData = [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error("[DistributorWiseSales] init error:", err?.response?.data || err);
        this.toast("error", "Failed to initialize page.");
      } finally {
        this.loading = false;
      }
    },

    // ========= DROPDOWNS =========
    async loadFsmList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const apiRes = await fetchFsmList(profileId, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        this.fsmList = body?.data || [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load FSM list.");
      }
    },

    async onFsmChange() {
      this.selectedAsm = "";
      this.selectedDistributor = "";
      this.asmList = [];
      this.distributorList = [];
      this.isDistributorLocked = false;
      this.searched = false;

      // clear optional filters + report
      this.selectedRoute = "";
      this.selectedRep = "";
      this.routeList = [];
      this.repList = [];
      this.items = [];
      this.reportData = [];

      if (this.selectedFsm === this.ALL) return;
      if (!this.selectedFsm) return;

      await this.loadAsmList();
    },

    async onAsmChange() {
      this.selectedDistributor = "";
      this.distributorList = [];
      this.isDistributorLocked = false;
      this.searched = false;

      // clear optional filters + report
      this.selectedRoute = "";
      this.selectedRep = "";
      this.routeList = [];
      this.repList = [];
      this.items = [];
      this.reportData = [];

      if (this.selectedAsm === this.ALL) return;
      if (!this.selectedAsm) return;

      await this.loadDistributorList();
    },


    //   try {
    //     const profileId = localStorage.getItem("profile_id");
    //     const res = await fetchAsmList(profileId, this.selectedFsm, this.token);
    //     if (this.handleUnauthorized(res)) return;

    //     const body = this.bodyOf(res);
    //     this.asmList = body?.data || [];
    //   } catch (err) {
    //     if (this.handleUnauthorized(err)) return;
    //     console.error(err);
    //     this.toast("error", "Failed to load ASM list.");
    //   }
    // },

    async loadAsmList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const apiRes = await fetchAsmList(profileId, this.selectedFsm, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        const list = body?.data || [];
        this.asmList = list;

        if (!list.length) {

          this.selectedAsm = "";
          this.selectedDistributor = "";
          this.distributorList = [];
          this.routeList = [];
          this.repList = [];
          return;
        }
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load ASM list.");
      }
    },



    async loadDistributorList() {
      try {
        const profileId = localStorage.getItem("profile_id");
        const apiRes = await fetchDistributorList(profileId, this.selectedAsm, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        let list = body?.data || [];

        if (!list.length) {

          this.selectedDistributor = "";
          this.distributorList = [];
          this.routeList = [];
          this.repList = [];
          return;
        }

        // Distributor login lock
        if (this.isDistributorUser && this.myProfileId) {
          const mine = list.find(d => Number(d.profile_id) === Number(this.myProfileId));
          if (mine) {
            list = [mine];
            this.selectedDistributor = String(mine.profile_id);
            this.isDistributorLocked = true;

            // programmatic set doesn't trigger @change
            await Promise.all([this.loadRouteList(), this.loadRepList()]);
          }
        }

        this.distributorList = list;
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err);
        this.toast("error", "Failed to load Distributor list.");
      }
    },


    async onDistributorChange() {
      this.selectedRoute = "";
      this.selectedRep = "";
      this.routeList = [];
      this.repList = [];
      this.items = [];
      this.reportData = [];
      this.searched = false;

      if (!this.selectedDistributor) return;
      if (this.selectedDistributor === this.ALL) return;

      await Promise.all([this.loadRouteList(), this.loadRepList()]);
    },




    async loadRouteList() {
      try {
        if (!this.selectedDistributor || this.selectedDistributor === this.ALL) {
          this.routeList = [];
          return;
        }

        const apiRes = await fetchRouteList(this.selectedDistributor, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        // adjust if your API shape differs
        this.routeList = body?.data?.data ?? body?.data ?? [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.routeList = [];
      }
    },

    async loadRepList() {
      try {
        if (!this.selectedDistributor || this.selectedDistributor === this.ALL) {
          this.repList = [];
          return;
        }

        const apiRes = await fetchSalesRepList(this.selectedDistributor, this.token);

        if (this.handleUnauthorized(apiRes)) return;
        const body = this.bodyOf(apiRes);
        if (this.handleUnauthorized(body)) return;

        this.repList = body?.data?.data ?? body?.data ?? [];
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.repList = [];
      }
    },



    //   try {
    //     if (!this.selectedDistributor || this.selectedDistributor === this.ALL) return;

    //     const res = await fetchSalesRepList(this.selectedDistributor, this.token);
    //     if (this.handleUnauthorized(res)) return;

    //     const body = this.bodyOf(res);
    //     this.repList = body?.data?.data ?? body?.data ?? [];
    //   } catch (err) {
    //     if (this.handleUnauthorized(err)) return;
    //     console.error("[RepList] error:", err?.response?.data || err);
    //     this.repList = [];
    //   }
    // },

    // ========= FORMATTERS =========



    formatQty(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0";
      return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
    },

    columnTotal(itemIdStr) {
      return this.reportData.reduce((sum, r) => sum + (Number(r?.[itemIdStr]) || 0), 0);
    },



    async performSearch() {
      this.token = localStorage.getItem("token_set") || "";
      if (!this.token) return this.redirectToLogin();

      if (!this.validateFiltersOneByOne()) return;
      if (!this.validateDatesForSearch()) return;


      this.loading = true;
      this.items = [];
      this.reportData = [];

      try {
        const payload = {
          start_date: this.startDate,
          end_date: this.endDate,
          fsm_profile_id: this.selectedFsm === this.ALL ? null : this.selectedFsm,
          asm_profile_id: this.selectedAsm === this.ALL ? null : this.selectedAsm,
          distributor_profile_id: this.selectedDistributor === this.ALL ? null : this.selectedDistributor,
          route_id: this.selectedRoute || null,
          rep_profile_id: this.selectedRep || null,
        };

        const apiRes = await fetchDissaleReport(this.token, payload);

        if (this.handleUnauthorized(apiRes)) return;
        const res = this.bodyOf(apiRes);
        if (this.handleUnauthorized(res)) return;

        if (!res || res.status !== true) {
          this.toast("error", res?.message || "Error loading report.");
          return;
        }

        const items = Array.isArray(res.items) ? res.items : [];
        const rows = Array.isArray(res.data) ? res.data : [];

        this.items = items
          .map(x => ({
            item_id: String(x.item_id),
            item_name: String(x.item_name ?? ""),
            sort_id: Number(x.sort_id ?? 0),
          }))
          .sort((a, b) => a.sort_id - b.sort_id);

        this.reportData = rows.map(r => {
          const out = { ...r };
          this.items.forEach(it => {
            const k = String(it.item_id);
            out[k] = Number(r?.[k]) || 0;
          });
          out.total_qty = Number(r?.total_qty) || 0;
          return out;
        });

        if (!this.reportData.length) {
          this.toast("error", "No data found for the selected filters.");
          return;
        }

        this.toast("success", "Report loaded successfully.");
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", err?.response?.data?.message || "Error loading report.");
      } finally {
        this.loading = false;
      }
    },


    // ========= EXPORT =========
    exportToExcel() {
      if (!this.reportData.length || !this.items.length) {
        this.toast("error", "No data to export.");
        return;
      }

      // helper to get filter names
      const getName = (list, id, field = "name") => {
        if (!id || id === this.ALL) return "All";
        const item = list.find(i => String(i.profile_id ?? i.id ?? i.route_id) === String(id));
        return item ? (item[field] || item.route_name || item.name) : "";
      };

      // ===== FILTER SECTION =====
      const filterSection = [
        ["Distributor Wise Sales Report"],
        [],
        ["FSM", getName(this.fsmList, this.selectedFsm)],
        ["ASM", getName(this.asmList, this.selectedAsm)],
        ["Distributor", getName(this.distributorList, this.selectedDistributor)],
        ["Route", getName(this.routeList, this.selectedRoute, "route_name")],
        ["Rep Name", getName(this.repList, this.selectedRep, "rep_name")],
        ["Start Date", this.startDate],
        ["End Date", this.endDate],
        [],
      ];

      // ===== HEADERS =====
      const headers = [
        "Distributor",
        "Route",
        "ASM/FSM/SE/Rep name",
        "Date",
        ...this.items.map(it => it.item_name),
        "Total Qty",
      ];

      const dataRows = this.reportData.map(r => ([
        r.distributor_name || "-",
        r.route_name || "-",
        r.rep_name || "-",
        r.invoice_date || "-",
        ...this.items.map(it => Number(r[String(it.item_id)]) || 0),
        Number(r.total_qty) || 0,
      ]));

      const totalRow = [
        "TOTAL", "", "", "",
        ...this.items.map(it => this.columnTotal(String(it.item_id))),
        this.grandTotalQty,
      ];

      const firstName = localStorage.getItem("first_name") || "";
      const lastName = localStorage.getItem("last_name") || "";
      const generatedBy = `${firstName} ${lastName}`.trim();
      const generatedAt = new Date().toLocaleString();

      const generatedRows = [
        [],
        ["Generated By", generatedBy],
        ["Generated At", generatedAt],
      ];

      const aoa = [...filterSection, headers, ...dataRows, totalRow, ...generatedRows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Distributor_Wise_Sales");
      XLSX.writeFile(wb, `Distributor_Wise_Sales_${this.startDate}_to_${this.endDate}.xlsx`);
      this.toast("success", "Excel exported successfully.");
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 10px;
}

/* Filters Layout - 2 Lines */
.filters-wrapper {
  width: 100%;
  margin-bottom: 20px;
}

.filters-row {
  display: grid;
  gap: 12px;
  align-items: end;
  width: 100%;
}

/* Line 1: 4 dropdowns */
.row-line-1 {
  grid-template-columns: repeat(4, 1fr);
  margin-bottom: 12px;
}

/* Line 2: other dropdowns, dates, and buttons */
.row-line-2 {
  grid-template-columns: 1fr 1fr 1fr auto auto;
}

.filter-item {
  width: 100%;
}

.filter-item.action-item {
  display: flex;
  align-items: flex-end;
}

/* Medium / tablet devices */
@media (max-width: 991px) and (min-width: 576px) {
  .row-line-1 {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .row-line-2 {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .row-line-2 .action-item .btn {
    width: 100%;
  }
}

/* Small devices (mobile) */
@media (max-width: 575px) {
  .row-line-1 {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .row-line-2 {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .row-line-2 .action-item .btn {
    width: 100%;
  }
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  color: #333;
}

select,
input[type="date"] {
  width: 100%;
  height: 40px;
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  box-sizing: border-box;
  background-color: #fff;
}

select:focus,
input[type="date"]:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
}

select:disabled,
input[type="date"]:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.btn {
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.btn.button-default {
  background-color: #007bff;
  color: #ffffff;
}

.btn.button-default:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn.button-default:disabled {
  background-color: #6c757d !important;
  color: #fff !important;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn.button-export {
  background-color: #28a745;
  color: #ffffff;
}

.btn.button-export:hover:not(:disabled) {
  background-color: #218838;
}

.btn.button-export:disabled {
  background-color: #6c757d !important;
  color: #fff !important;
  cursor: not-allowed;
  opacity: 0.7;
}

/* ===== Table Scroll + Sticky Header ===== */
.table-scroll {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid #ddd;
  margin-top: 10px;
  position: relative;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.table th,
.table td {
  padding: 12px 16px;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
  font-size: 0.85rem;
}

.table thead th {
  background: #f8f9fa !important;
  color: #333 !important;
  font-weight: 700;
  line-height: 20px;
  height: 42px;
  vertical-align: middle;
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 2px solid #ddd;
  text-transform: uppercase;
}

.total-row td {
  background: #f1f3f5;
  font-weight: 700;
  border-top: 2px solid #495057;
}

.text-center {
  text-align: center;
}

.text-end {
  text-align: right;
}

/* =========================================================
   LOADING STATE (Matching dayreport_form.vue)
========================================================= */
.loading-container {
  width: 100%;
}

.loading-card {
  text-align: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 42px 24px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.loading-spinner-wrapper {
  margin-bottom: 12px;
}

.loading-icon {
  font-size: 32px;
  color: #007bff;
}

.loading-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
}

.loading-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 0;
}

/* =========================================================
   EMPTY STATE (Matching dayreport_form.vue)
========================================================= */
.no-data-container {
  width: 100%;
  margin-top: 24px;
}

.no-data-card {
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 48px 32px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;
}

.no-data-card:hover {
  border-color: #94a3b8;
  background: #f1f5f9;
}

.no-data-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px auto;
  color: #64748b;
  font-size: 32px;
  transition: transform 0.2s ease;
}

.no-data-card:hover .no-data-icon-wrapper {
  transform: scale(1.05);
  background: #dbeafe;
  color: #2563eb;
}

.no-data-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #64748b;
  max-width: 640px;
  margin: 0 auto 16px auto;
  line-height: 1.5;
}
</style>